import { Skeleton, Stack } from '@mui/material';
import {
  DatePicker,
  DateValidationError,
  LocalizationProvider,
  PickerChangeHandlerContext,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { startOfMonth } from 'date-fns';
import { useState } from 'react';

import { AsyncRenderer } from '@client/new/components/AsyncRenderer/AsyncRenderer';
import { Button } from '@design-system/components/button/button';
import { sprinkles } from '@design-system/sprinkles.css';
import { fontFamily } from '@design-system/tokens';

import { OtpRevenue } from './OtpRevenue';
import { SubscriptionRevenue } from './SubscriptionRevenue';
import { usePayouts } from './usePayouts';
import { useTransactionLedger } from './useTransactionLedger';

type DashboardProps = {
  creatorId: number;
};

export const Dashboard: React.FC<DashboardProps> = ({ creatorId }) => {
  const month = startOfMonth(new Date());
  const [date, setDate] = useState(startOfMonth(month));
  const [error, setError] = useState(false);
  const payouts = usePayouts({ date, creatorId });
  const { isExporting, exportTransactions } = useTransactionLedger();
  const showDownloadTransactionsButton =
    payouts.type === 'fetched' && payouts.value.exportEnabled;

  const handleDateChange = (
    value: Date | null,
    context: PickerChangeHandlerContext<DateValidationError>,
  ) => {
    if (!context.validationError && value) {
      setDate(value);
      setError(false);
    } else {
      setError(true);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className="px-[30px] py-[40px] bg-white rounded-xl">
        <Stack direction="row" justifyContent="space-between">
          <DatePicker
            views={['year', 'month']}
            openTo="month"
            value={date}
            onChange={handleDateChange}
            minDate={new Date('2023-08-01')}
            maxDate={new Date()}
            disableFuture
            slotProps={{
              field: {
                readOnly: true,
              },
              popper: {
                sx: {
                  '& .MuiPickersMonth-monthButton': {
                    fontFamily: fontFamily.visuelt,
                  },
                  '& .MuiPickersYear-yearButton': {
                    fontFamily: fontFamily.visuelt,
                  },
                  '& .MuiPickersCalendarHeader-label': {
                    fontFamily: fontFamily.visuelt,
                  },
                },
              },
              textField: {
                InputProps: {
                  sx: { fontFamily: fontFamily.visuelt },
                },
                error,
                helperText: error
                  ? 'The data is only available since August 2023.'
                  : undefined,
              },
            }}
            className={sprinkles({ fontFamily: 'visuelt' })}
          />
          {showDownloadTransactionsButton && (
            <Button
              size="medium"
              variant="text"
              color="neutral"
              loading={isExporting}
              onClick={() => exportTransactions(creatorId, date)}
              disabled={isExporting}
            >
              Download All Transactions
            </Button>
          )}
        </Stack>
        <div>
          <AsyncRenderer
            data={payouts}
            loadRender={
              <>
                <Skeleton
                  variant="rectangular"
                  sx={{
                    width: '100%',
                    height: 30,
                    marginBottom: '10px',
                    marginTop: '10px',
                  }}
                />
                <Skeleton
                  variant="rectangular"
                  sx={{ width: '100%', height: 90 }}
                />
              </>
            }
            renderer={(data) => (
              <>
                <section className="mt-[30px]">
                  <SubscriptionRevenue {...data.subscription} />
                </section>
                <section className="mt-[30px]">
                  <OtpRevenue {...data.otp} />
                </section>
              </>
            )}
          />
        </div>
      </div>
    </LocalizationProvider>
  );
};
